import { point, nearestPointOnLine, bearing as turfBearing } from "@turf/turf";
import {
  SNAP_TOLERANCE_M,
  HEADING_TOLERANCE_DEG,
  CORNERING_APPROACH_TOLERANCE_DEG,
  CORNERING_SLACK_DEG
} from "./constants";
import { bboxAround, signedBearingDifference } from "./geo";

/** Travel directions permitted on a way: 1 = along coordinate order, -1 = against it. */
export function allowedDirections(way) {
  if (way.oneway === 1) {
    return [1];
  }
  if (way.oneway === -1) {
    return [-1];
  }
  return [1, -1];
}

/**
 * Acceptable range of the road's travel bearing relative to the event heading, as
 * [minClockwise, maxClockwise] degrees. Symmetric for most events; one-sided for
 * cornering events, whose heading was recorded part-way through the turn.
 */
export function headingWindow(event, { headingTolerance = HEADING_TOLERANCE_DEG, approachTolerance = CORNERING_APPROACH_TOLERANCE_DEG, slack = CORNERING_SLACK_DEG } = {}) {
  if (event.event_type === "cornering_left") {
    return [-slack, approachTolerance];
  }
  if (event.event_type === "cornering_right") {
    return [-approachTolerance, slack];
  }
  return [-headingTolerance, headingTolerance];
}

/**
 * Snap an event to the most plausible road, taking the event heading into account.
 * @param {Object} event normalized event (lon, lat, heading)
 * @param {Object} index road index from buildRoadIndex
 * @return {Object|null} { way, direction, location, snapped, distance, bearingDiff } or null when nothing qualifies
 */
export function matchEvent(event, index, options = {}) {
  const { snapTolerance = SNAP_TOLERANCE_M, headingTolerance = HEADING_TOLERANCE_DEG } = options;
  const location = [event.lon, event.lat];
  const pt = point(location);
  const hasHeading = Number.isFinite(event.heading);
  const [minRotation, maxRotation] = headingWindow(event, options);
  // a full-tolerance heading mismatch costs as much as a full-tolerance snap distance
  const bearingWeight = snapTolerance / headingTolerance;

  let best = null;
  for (const way of index.search(bboxAround(location, snapTolerance))) {
    const snapped = nearestPointOnLine(way.feature, pt, { units: "meters" });
    const distance = snapped.properties.dist;
    if (distance > snapTolerance) {
      continue;
    }
    const segmentIndex = Math.min(snapped.properties.index, way.coords.length - 2);
    const segmentBearing = turfBearing(way.coords[segmentIndex], way.coords[segmentIndex + 1]);

    for (const direction of allowedDirections(way)) {
      const travelBearing = direction === 1 ? segmentBearing : segmentBearing + 180;
      const rotation = hasHeading ? signedBearingDifference(event.heading, travelBearing) : 0;
      if (rotation < minRotation || rotation > maxRotation) {
        continue;
      }
      const bearingDiff = Math.abs(rotation);
      const score = distance + bearingDiff * bearingWeight;
      if (!best || score < best.score) {
        best = {
          way,
          direction,
          location: snapped.properties.location,
          snapped: snapped.geometry.coordinates,
          distance,
          bearingDiff,
          score
        };
      }
    }
  }
  return best;
}
